// src/components/modals/AboutModal.tsx
import React from 'react';
import { Info, Zap, Shield, Users, X } from 'lucide-react';
import type { ModalProps } from '../../types/components';

export const AboutModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Info className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">About Dairy Translate</h1>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">What is Dairy Translate?</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Dairy Translate is an AI-powered assistant that helps dairy farmers and veterinarians 
              convert plain English descriptions into DC305 commands. Whether you're looking to 
              analyze milk production, track breeding records, or monitor herd health, our tool 
              makes it easy to generate the exact DC305 code you need.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Key Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Zap className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Instant Translation</h3>
                  <p className="text-gray-700">
                    Convert natural language queries into precise DC305 commands instantly using 
                    advanced AI technology.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Accurate Results</h3>
                  <p className="text-gray-700">
                    Our AI is trained specifically on DC305 syntax and dairy management practices 
                    to ensure reliable, production-ready commands.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Users className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Easy to Use</h3>
                  <p className="text-gray-700">
                    No need to memorize complex DC305 syntax. Simply describe what you want 
                    to accomplish in plain English.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Info className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Demo Examples</h3>
                  <p className="text-gray-700">
                    Try our built-in demo examples to see how common dairy management tasks 
                    translate into DC305 commands.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <p className="text-gray-700">Type or speak your dairy management query in plain English</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <p className="text-gray-700">Our AI analyzes your request and generates the appropriate DC305 command</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <p className="text-gray-700">Copy the generated command and use it directly in your DC305 system</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Perfect For</h2>
            <ul className="grid md:grid-cols-2 gap-2 text-gray-700">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                Dairy farmers and farm managers
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                Veterinarians working with dairy herds
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                Farm consultants and advisors
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                Students learning DC305 commands
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                Anyone working with DC305 systems
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                Herd health management teams
              </li>
            </ul>
          </section>

          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Get Started Today</h2>
            <p className="text-gray-700 mb-4">
              Ready to streamline your dairy management workflow? Try Dairy Translate now with our 
              free demo examples, or start typing your own queries to see the power of AI-assisted 
              DC305 command generation.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Using Dairy Translate
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};